import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import LinkIcon from '@atlaskit/icon/core/link';
import Tooltip from '@atlaskit/tooltip';
import { usePlatformInstrumentation } from '@atlassian/platform-analytics';

import { useAudioPlayerStore } from '../../hooks/useAudioPlayerStore';
import { useAudioShareLinkCopy } from '../../hooks/useAudioShareLinkCopy';

const messages = defineMessages({
	tooltip: {
		id: 'audio.audio-player.audio-share-link-button.link',
		defaultMessage: 'Link to audio',
		description: 'Tooltip for the audio share link button',
	},
	label: {
		id: 'audio.audio-player.audio-share-link-button.label',
		defaultMessage: 'Copy link to audio',
		description: 'Label for the audio share link button',
	},
	copied: {
		id: 'audio.audio-player.audio-share-link-button.copied',
		defaultMessage: 'Copied!',
		description: 'Tooltip text shown after the link has been copied to clipboard',
	},
});

export const AudioShareLinkButton = () => {
	const { formatMessage } = useIntl();
	const { activeContent, activeFeature } = useAudioPlayerStore();
	const { copyAudioShareLinkToClipboard } = useAudioShareLinkCopy();
	const { sendUIEvent } = usePlatformInstrumentation();

	const defaultTooltip = formatMessage(messages.tooltip);
	const [tooltipContent, setTooltipContent] = useState(defaultTooltip);
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const updateTooltip = useRef<() => void>();

	useLayoutEffect(() => {
		updateTooltip.current?.();
	}, [tooltipContent]);

	const handleCopyLink = useCallback(() => {
		copyAudioShareLinkToClipboard(
			activeContent.mainContentLink,
			activeFeature,
			activeContent.supportingContent,
		);
		sendUIEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'aiAudioCopyLink',
			attributes: {
				contentType: activeContent.contentType,
				trackType: activeFeature,
			},
		});

		setTooltipContent(formatMessage(messages.copied));

		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}
		timeoutRef.current = setTimeout(() => {
			setTooltipContent(formatMessage(messages.tooltip));
		}, 2000);
	}, [activeContent, activeFeature, copyAudioShareLinkToClipboard, sendUIEvent, formatMessage]);

	return (
		<div id="audio-player-changeboarding-step-3">
			<Tooltip
				content={({ update }) => {
					updateTooltip.current = update;
					return tooltipContent;
				}}
				position="bottom"
			>
				<IconButton
					icon={LinkIcon}
					label={formatMessage(messages.label)}
					onClick={handleCopyLink}
					isDisabled={activeContent.mainContentLink === ''}
					shape="circle"
				/>
			</Tooltip>
		</div>
	);
};
